import React, { useEffect } from "react";
import { X } from "lucide-react";

/**
 * Full-screen screenshot viewer. Closes on Escape, on overlay click, or on X.
 * Props:
 *   open: boolean
 *   src: data URL or http(s) image src
 *   caption: { symbol, date, side } | null
 *   onClose: () => void
 */
export default function ScreenshotLightbox({ open, src, caption, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    // Prevent body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const sideLabel = caption?.side === "buy" ? "BUY Long" : caption?.side === "sell" ? "SELL Short" : "";
  const niceDate = caption?.date
    ? new Date(caption.date + "T00:00:00Z").toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric", timeZone: "UTC",
      })
    : "";

  return (
    <div
      data-testid="screenshot-lightbox"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center p-4 sm:p-8"
      style={{
        background: "rgba(0, 0, 0, 0.9)",
        animation: "btFadeIn 200ms ease",
      }}
    >
      <style>{`
        @keyframes btFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes btScaleIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
      `}</style>

      <button
        type="button"
        onClick={onClose}
        data-testid="screenshot-lightbox-close"
        aria-label="Close screenshot viewer"
        className="absolute top-4 right-4 inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors hover:bg-white/10"
        style={{ color: "#ffffff" }}
      >
        <X className="w-6 h-6" strokeWidth={2} />
      </button>

      {src ? (
        <img
          src={src}
          alt="Trade chart screenshot"
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: "90vw",
            maxHeight: "82vh",
            objectFit: "contain",
            borderRadius: "8px",
            boxShadow: "0 24px 60px rgba(0, 0, 0, 0.6)",
            animation: "btScaleIn 200ms ease",
          }}
        />
      ) : (
        <div className="text-white text-sm">No screenshot available</div>
      )}

      {caption && (
        <p
          data-testid="screenshot-lightbox-caption"
          onClick={(e) => e.stopPropagation()}
          className="mt-5 font-mono text-sm text-white/90 px-4 py-2 rounded-md"
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          {caption.symbol}{niceDate ? ` · ${niceDate}` : ""}{sideLabel ? ` · ${sideLabel}` : ""}
        </p>
      )}
    </div>
  );
}
